import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import VideoProgressManager from "./videoProgress.js";

const videoProgressManager = new VideoProgressManager();

// Sanitize YouTube ID - remove query parameters, fragments, and whitespace
const sanitizeYouTubeId = (id) => {
  if (!id) return null;
  // Remove query parameters (?si=...) and fragments (#...)
  const cleanId = id.split("?")[0].split("#")[0].trim();
  // YouTube IDs are 6-20 alphanumeric characters, hyphens, underscores
  const match = cleanId.match(/^[a-zA-Z0-9_-]{6,20}$/);
  return match ? match[0] : cleanId;
};

const YouTubeNativePlayer = forwardRef(
  (
    {
      videoId,
      onLoadingChange,
      onTimeUpdate,
      onFullscreenChange,
      onFullscreenError,
      onEnded,
      onPlaying,
      isMobile,
      autoplay = false, // Default: no autoplay (for inline blocks)
      skipResume = false, // When true, always start from beginning (Play Next auto-advance)
    },
    ref,
  ) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    // Use ref instead of state to avoid re-renders and player reinitialization
    const hasResumedRef = useRef(false);

    const cleanVideoId = sanitizeYouTubeId(videoId);

    // Reset hasResumed when videoId changes
    useEffect(() => {
      hasResumedRef.current = false;
    }, [cleanVideoId]);

    // Detect iOS devices (including iPad on iOS 13+)
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // Load YouTube IFrame API
    useEffect(() => {
      if (!window.YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.async = true;
        document.head.appendChild(tag);
      }
    }, []);

    // Handle player ready
    const handleReady = useCallback(
      (event) => {
        setIsReady(true);
        onLoadingChange?.(false);

        const player = event.target;

        // Resume from saved progress if available (works for both iOS and desktop)
        // Skip resume when coming from Play Next auto-advance (always start fresh)
        if (!hasResumedRef.current && !skipResume) {
          hasResumedRef.current = true;
          const resumeTime = videoProgressManager.getResumeTime(cleanVideoId);
          if (resumeTime > 0) {
            // Wait a bit for player to be fully ready before seeking
            setTimeout(() => {
              try {
                player.seekTo(resumeTime, true);
                console.log(`Resumed video ${cleanVideoId} from ${resumeTime}s`);
              } catch (e) {
                console.warn('Error seeking to resume time:', e);
              }
            }, 500);
          }
        }

        // Only autoplay if explicitly enabled and not iOS
        if (autoplay && !isIOS) {
          player.playVideo();
        }
      },
      [cleanVideoId, onLoadingChange, isIOS, autoplay, skipResume],
    );

    // Handle player state changes
    const handleStateChange = useCallback(
      (event) => {
        // YT.PlayerState: ENDED=0, PLAYING=1, PAUSED=2, BUFFERING=3, CUED=5
        if (event.data === 0) {
          // ENDED - video finished playing
          onEnded?.();
        } else if (event.data === 1) {
          // PLAYING - video started playing
          onLoadingChange?.(false);
          onPlaying?.();
        }
      },
      [onLoadingChange, onEnded, onPlaying],
    );

    // Initialize player when API ready
    useEffect(() => {
      if (!cleanVideoId) return;

      const initPlayer = () => {
        if (!containerRef.current) return;

        // Destroy existing player if any
        if (playerRef.current && typeof playerRef.current.destroy === "function") {
          try {
            playerRef.current.destroy();
          } catch (e) {
            console.warn("Error destroying existing player:", e);
          }
        }

        // Autoplay only if explicitly enabled AND not iOS
        const shouldAutoplay = autoplay && !isIOS;

        playerRef.current = new window.YT.Player(containerRef.current, {
          videoId: cleanVideoId,
          playerVars: {
            autoplay: shouldAutoplay ? 1 : 0,
            mute: 0,
            playsinline: 1, // Prevents iOS from opening native player
            rel: 0,
            modestbranding: 1,
            fs: 1, // Enable fullscreen for all platforms including iOS
            controls: 1, // Native YouTube controls with fullscreen button
          },
          events: {
            onReady: handleReady,
            onStateChange: handleStateChange,
          },
        });
      };

      if (window.YT && window.YT.Player) {
        initPlayer();
      } else {
        window.onYouTubeIframeAPIReady = initPlayer;
      }

      return () => {
        if (playerRef.current && typeof playerRef.current.destroy === "function") {
          try {
            playerRef.current.destroy();
          } catch (e) {
            console.warn("Error destroying player on cleanup:", e);
          }
        }
      };
    }, [cleanVideoId, handleReady, handleStateChange, isIOS, autoplay]);

    // Time update interval for progress tracking
    useEffect(() => {
      if (!isReady) return;

      const interval = setInterval(() => {
        const player = playerRef.current;
        if (player && typeof player.getCurrentTime === "function") {
          try {
            const currentTime = player.getCurrentTime();
            const duration = player.getDuration();

            if (currentTime > 0 && duration > 0) {
              onTimeUpdate?.(currentTime);
              videoProgressManager.updateProgress(
                cleanVideoId,
                currentTime,
                duration,
              );
            }
          } catch (error) {
            console.warn("Error getting video time:", error);
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [isReady, cleanVideoId, onTimeUpdate]);

    // Listen for fullscreen changes
    useEffect(() => {
      const handleFullscreenChange = () => {
        const isFullscreen =
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement;

        onFullscreenChange?.(isFullscreen);
      };

      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);

      return () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullscreenChange,
        );
        document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
        document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
      };
    }, [onFullscreenChange]);

    // Expose ref methods
    useImperativeHandle(
      ref,
      () => ({
        get currentTime() {
          try {
            return playerRef.current?.getCurrentTime() || 0;
          } catch (error) {
            console.warn("Error getting currentTime:", error);
            return 0;
          }
        },
        set currentTime(time) {
          try {
            if (playerRef.current && typeof playerRef.current.seekTo === "function") {
              playerRef.current.seekTo(time, true);
            }
          } catch (error) {
            console.warn("Error setting currentTime:", error);
          }
        },
        seek(time) {
          try {
            if (playerRef.current && typeof playerRef.current.seekTo === "function") {
              playerRef.current.seekTo(time, true);
            }
          } catch (error) {
            console.warn("Error seeking:", error);
          }
        },
        get ended() {
          try {
            return playerRef.current?.getPlayerState() === 0; // YT.PlayerState.ENDED
          } catch (error) {
            console.warn("Error checking ended state:", error);
            return false;
          }
        },
        get duration() {
          try {
            return playerRef.current?.getDuration() || 0;
          } catch (error) {
            console.warn("Error getting duration:", error);
            return 0;
          }
        },
        play() {
          try {
            if (playerRef.current && typeof playerRef.current.playVideo === "function") {
              playerRef.current.playVideo();
            }
          } catch (error) {
            console.warn("Error playing video:", error);
          }
        },
        pause() {
          try {
            if (playerRef.current && typeof playerRef.current.pauseVideo === "function") {
              playerRef.current.pauseVideo();
            }
          } catch (error) {
            console.warn("Error pausing video:", error);
          }
        },
        toggleFullscreen() {
          try {
            const iframe = containerRef.current?.querySelector("iframe");
            if (!iframe) return;

            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else if (document.webkitFullscreenElement) {
              document.webkitExitFullscreen();
            } else if (document.mozFullScreenElement) {
              document.mozCancelFullScreen();
            } else if (document.msFullscreenElement) {
              document.msExitFullscreen();
            } else {
              if (iframe.requestFullscreen) {
                iframe.requestFullscreen();
              } else if (iframe.webkitRequestFullscreen) {
                iframe.webkitRequestFullscreen();
              } else if (iframe.mozRequestFullScreen) {
                iframe.mozRequestFullScreen();
              } else if (iframe.msRequestFullscreen) {
                iframe.msRequestFullscreen();
              }
            }
          } catch (error) {
            console.warn("Error toggling fullscreen:", error);
            onFullscreenError?.(error);
          }
        },
        unmute() {
          try {
            if (playerRef.current && typeof playerRef.current.unMute === "function") {
              playerRef.current.unMute();
            }
          } catch (error) {
            console.warn("Error unmuting video:", error);
          }
        },
      }),
      [],
    );

    if (!cleanVideoId) {
      return React.createElement("div", {
        className: "youtube-native-player",
      }, "No video ID provided");
    }

    return (
      <div
        ref={containerRef}
        className="youtube-native-player"
      />
    );
  },
);

YouTubeNativePlayer.displayName = "YouTubeNativePlayer";

export default YouTubeNativePlayer;
